import { useEffect, useRef } from 'react'
import { usePOSStore } from '@/stores/posStore'
import { v2, v2api } from '@/api/v2'
import { RECEIPT_STATUS } from '@/api/receipts'
import type { Receipt } from '@/api/receipts'

// Statuses where a register still bound to the receipt is a ghost: the
// sale is settled (or destroyed) server-side and can never be tendered
// again. PAYING is deliberately absent -- a mid-terminal-payment
// receipt must STAY bound so the checkout flow's register_busy
// recovery can resume the live session instead of orphaning it.
const GHOST_STATUSES = new Set<number>([
  RECEIPT_STATUS.PAID,
  RECEIPT_STATUS.DELETED,
  RECEIPT_STATUS.CANCELED,
])

/**
 * Boot-time bound-receipt reconcile.
 *
 * The register persists its open receipt binding (id + cart + customer)
 * across reloads, but rehydration never asks the server whether that
 * receipt is still open. When the UI dies mid-payment -- logout, crash,
 * tab close while the server-owned terminal session completes -- the
 * register comes back showing a PAID sale as an open cart ("unpaid but
 * also paid", Orleans 2026-07-14). The settlement-time resets in
 * CheckoutModal can't cover this class: they need the modal alive at
 * the moment the session settles.
 *
 * One-shot on register mount: fetch the bound receipt; if the server
 * says it's paid / deleted / canceled, silently clear the register --
 * the sale is in Orders where it belongs. Fetch errors leave state
 * alone (offline boot etc.); the checkout flow's already-paid guards
 * remain the backstop.
 */
export function useBoundReceiptReconcile() {
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    const { currentReceiptId, isRefundMode } = usePOSStore.getState()
    if (!currentReceiptId || isRefundMode) return

    void v2api
      .get<{ receipt?: Receipt } & Receipt>(v2(`/receipts/${currentReceiptId}`))
      .then((live) => {
        const receipt = (live?.receipt ?? live) as Receipt
        const state = usePOSStore.getState()
        // The cashier may have recalled or rebound while the fetch was
        // in flight -- only clear the binding we actually checked.
        if (state.currentReceiptId !== currentReceiptId) return
        if (GHOST_STATUSES.has(Number(receipt?.status))) {
          state.clearReceipt()
        }
      })
      .catch(() => {
        // Offline boot / transient error -- leave the register as-is;
        // the checkout flow's already-paid handling is the backstop.
      })
  }, [])
}
